import type { VoidFunctionComponent } from 'react';

import { renderRevision } from '../../../../shared/helpers/renderLocator';
import Locator from '../../../../shared/Locator';
import { UNKNOWN_VERSION } from '../../../../shared/organizationPackages/helpers';
import { useIsQualityEnabled } from '../../../helpers/features/quality';
import { nuc } from '../../../utils';
import Flex from '../../generic/Flex';
import Checkbox from '../../generic/Forms/Checkbox';
import List from '../../generic/List';
import UnknownStatePanel from '../../projects/dependenciesV2/DependenciesTable/DependencyTableRow/StateContextPanel/UnknownStatePanel';
import PackageLabelsList from '../../shared/packageLabels/PackageLabelsList';
import { PackageInventoryLicenses } from '../shared';
import type { PackageLabelAssignmentWithName } from '../../../../shared/model-typings/packagelabelassignment';
import type { PackageDetails } from '../../../../shared/organizationPackages/response';

import VulnSummary from './VulnSummary';

import css from './PackageDetailsTable.module.less';

/**
 * If there are no blocked projects, we simply display the count.
 * If some of the projects are blocked, we show the blocked count
 * beside the project count. If all are blocked, we display "all blocked"
 */
const getProjectCountText = (details: PackageDetails): number | `${number} (all blocked)` | `${number} (${number} blocked)` => {
  if (!details.blockedProjectCount) {
    return details.numberOfProjects;
  }
  if (details.numberOfProjects === details.blockedProjectCount) {
    return `${details.numberOfProjects} (all blocked)`;
  }
  return `${details.numberOfProjects} (${details.blockedProjectCount} blocked)`;
};

type Props = PackageDetails & {
  pathname: string;
  isSelected: boolean;
  onToggleVersion: (packageRevision: string) => void;
  versionQueryString: string;
};

/**
 * PackageDetailsTableRow displays specific version info, including
 * the revision/semver tag, project count, licenses, and vulns.
 * It acts as a link to the version details page (/packages/:locator/:version/projects).
 */
const PackageDetailsTableRow: VoidFunctionComponent<Props> = ({
  pathname,
  isSelected,
  onToggleVersion,
  versionQueryString,
  ...details
}) => {
  const { fetcher, revision } = Locator.parse(details.packageRevision);
  const isVulnSummaryEnabled = fetcher !== 'csbinary';

  //  The package denial workflow is applied to quality policies, so they must have this enabled.
  const isQualityEnabled = useIsQualityEnabled();
  const body = (
    <>
      <div className={css.versionSummaryContainer}>
        <Flex justify="space-between">
          <List.RowTitle>{details.semverTag || renderRevision(details.packageRevision) || UNKNOWN_VERSION}</List.RowTitle>
          { isVulnSummaryEnabled && <VulnSummary packageDetails={details} /> }
        </Flex>
      </div>
      <List.RowDetails>
        <List.RowDetailsItem
          trailingSlot={<List.RowDetailsIconItemBadge>{getProjectCountText(details)}</List.RowDetailsIconItemBadge>}
        >
          Projects
        </List.RowDetailsItem>
        <PackageInventoryLicenses licenses={details.licenses} />
        {(details.packageLabels && details.packageLabels.length > 0) && (
          <PackageLabelsList labels={details.packageLabels as PackageLabelAssignmentWithName[]} />
        )}
      </List.RowDetails>
      {!revision && (
        <div className="margin-top-sm">
          <UnknownStatePanel revision={details.packageRevision} />
        </div>
      )}
    </>
  );

  return (
    <List.RowContainer>
      <List.Row
        to={{
          pathname: `${pathname}/${nuc(revision ?? UNKNOWN_VERSION)}/projects`,
          search: `?${versionQueryString}`,
        }}
        key={`${pathname}/${revision}`}
        flexDirection={isQualityEnabled ? 'row' : 'column'}
        hasCheckbox={isQualityEnabled}
        testId="package-inventory-package-table-row--versions"
    >
        {!isQualityEnabled ? body : (
          <>
            <Checkbox
              isChecked={isSelected}
              onChange={() => onToggleVersion(details.packageRevision)}
              isRippleDisabled
              shouldStopPropagation
            />
            <div className="width-full padding-left-sm padding-right-sm">
              {body}
            </div>
          </>
        )}
      </List.Row>
    </List.RowContainer>
  );
};

export default PackageDetailsTableRow;
